import React from 'react'
import styled from 'styled-components'
import { accents, brand, gray } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { radii } from 'sutra/tokens/radii'
import { spacing } from 'sutra/tokens/spacing'

import type { BenchmarkingEntityType } from 'src/components/agent-forms/BenchmarkingAgentConfigForm'
import { Label } from 'src/components/agent-forms/styles'

export interface BenchmarkEntityRadioGroupProps {
  value: BenchmarkingEntityType | null
  onChange: (value: BenchmarkingEntityType) => void
}

const ENTITY_OPTIONS: Array<{ value: BenchmarkingEntityType; label: string }> =
  [
    { value: 'brand', label: 'Brands' },
    { value: 'product_hierarchy', label: 'Products' },
  ]

/**
 * "What would you like to benchmark?" + a Brands/Products radio-card pair,
 * left-aligned above the Universe card — it decides the entity vocabulary
 * for the whole panel (Universe + Focus), not just the nested Focus card, so
 * it lives outside both.
 *
 * Modeled on `SensitivityControl`'s radio-card pattern (sutra has no Radio
 * primitive either) minus the per-option caption/footer — there's no
 * description to show for a binary Brands/Products choice.
 *
 * `value === null` (reachable only pre-hydration on the edit/staff surfaces)
 * renders with neither card checked.
 */
export const BenchmarkEntityRadioGroup: React.FC<
  BenchmarkEntityRadioGroupProps
> = ({ value, onChange }) => (
  <Wrapper>
    <Label>What would you like to benchmark?</Label>
    <Group role="radiogroup" aria-label="What would you like to benchmark?">
      {ENTITY_OPTIONS.map((opt) => (
        <Card
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={value === opt.value}
          $active={value === opt.value}
          onClick={() => onChange(opt.value)}
        >
          <Dot $active={value === opt.value} />
          <OptionLabel>{opt.label}</OptionLabel>
        </Card>
      ))}
    </Group>
  </Wrapper>
)

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]}px;
`

const Group = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${spacing[2]}px;
`

const Card = styled.button<{ $active: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  gap: ${spacing[2]}px;
  padding: ${spacing[3]}px;
  border-radius: ${radii.sm}px;
  text-align: left;
  cursor: pointer;
  border: 1px solid
    ${({ $active }) => ($active ? accents.reflectionBlue[500] : gray[200])};
  background: ${({ $active }) =>
    $active ? accents.reflectionBlue[50] : brand.white};
  transition:
    background ${motion.duration.fast}ms ${motion.ease},
    border-color ${motion.duration.fast}ms ${motion.ease};

  &:hover {
    border-color: ${({ $active }) =>
      $active ? accents.reflectionBlue[500] : gray[300]};
    background: ${({ $active }) =>
      $active ? accents.reflectionBlue[50] : gray[50]};
  }

  &:focus-visible {
    outline: 2px solid ${accents.reflectionBlue[500]};
    outline-offset: 2px;
  }
`

const Dot = styled.span<{ $active: boolean }>`
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: ${radii.pill}px;
  border: 2px solid
    ${({ $active }) => ($active ? accents.reflectionBlue[500] : gray[300])};
  background: ${({ $active }) =>
    $active ? accents.reflectionBlue[500] : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: ${radii.pill}px;
    background: ${brand.white};
    opacity: ${({ $active }) => ($active ? 1 : 0)};
  }
`

const OptionLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${gray[900]};
`
